import { useState, useEffect, useRef } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import Hero from '../content/pricing/hero';
import PlanCards from '../content/pricing/plan-cards';
import { loadCheckoutScript, fetchPricingData, getAvailableCycles } from '../content/pricing/helpers';
import { trackBeforeCheckout, getFreemiusPricing, getPricingPlan } from '../helpers';

const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 99999,
};

const PricingModal = ({ isOpen, onClose }) => {
    const config = getFreemiusPricing();
    const [adminBarTop, setAdminBarTop] = useState(0);
    const [pricingData, setPricingData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [billingCycle, setBillingCycle] = useState('annual');
    const [checkingOut, setCheckingOut] = useState(null);
    const fsHandlerRef = useRef(null);
    const [isMaximized, setIsMaximized] = useState(false);

    const currency = config?.currency || 'usd';
    useEffect(() => {
        const calcAdminBarTop = () => {
            try {
                const el = document.getElementById('wpadminbar');
                const h = el ? Math.ceil(el.getBoundingClientRect().height) : 0;
                setAdminBarTop(h);
            } catch (e) {
                setAdminBarTop(0);
            }
        };

        calcAdminBarTop();
        window.addEventListener('resize', calcAdminBarTop);
        window.addEventListener('orientationchange', calcAdminBarTop);

        return () => {
            window.removeEventListener('resize', calcAdminBarTop);
            window.removeEventListener('orientationchange', calcAdminBarTop);
        };
    }, []);
    useEffect(() => {
        if (!isOpen) setIsMaximized(false);
    }, [isOpen]);
    useEffect(() => {
        if (!isOpen) return;
        if (!config) { setLoading(false); return; }

        const cache = window.JkitPricingCache || (window.JkitPricingCache = {});
        if (cache.data) {
            setPricingData(cache.data);
            if (cache.cycles && cache.cycles.length > 0) setBillingCycle(cache.cycles[0]);
            setLoading(false);
            return;
        }

        setLoading(true);
        fetchPricingData(config)
            .then((data) => {
                cache.data = data;
                cache.cycles = getAvailableCycles(data.plans || []);

                setPricingData(data);
                if (data.plans) {
                    const cycles = getAvailableCycles(data.plans);
                    if (cycles.length > 0) setBillingCycle(cycles[0]);
                }
                setLoading(false);
            })
            .catch((err) => {
                setError(err.message);
                setLoading(false);
            });
    }, [isOpen]);

    const handleCheckout = async (plan, pricingEntry) => {
        setCheckingOut(pricingEntry.id);
        await loadCheckoutScript(config.is_production);

        if (!window.FS || !window.FS.Checkout) {
            setCheckingOut(null);
            return;
        }

        if (!fsHandlerRef.current) {
            fsHandlerRef.current = window.FS.Checkout.configure({
                plugin_id: config.plugin_id,
                public_key: config.plugin_public_key,
                ...(config.sandbox && { sandbox: config.sandbox }),
                ...(config.s_ctx_type && { s_ctx_type: config.s_ctx_type }),
                ...(config.s_ctx_id && { s_ctx_id: config.s_ctx_id }),
                ...(config.s_ctx_ts && { s_ctx_ts: config.s_ctx_ts }),
                ...(config.s_ctx_secure && { s_ctx_secure: config.s_ctx_secure }),
                ...(config.home_url && { home_url: config.home_url }),
                ...(config.next && { next: config.next }),
                ...(config.is_network_admin && { is_network_admin: config.is_network_admin }),
                ...(config.plugin_version && { plugin_version: config.plugin_version }),
            });
        }

        const pricingPlan = getPricingPlan();
        const resolvedCoupon = (() => {
            if (!pricingPlan) return config.default_coupon || null;
            const id = String(pricingEntry.id);
            for (const key of ['basic', 'professional', 'agency']) {
                const p = pricingPlan[key];
                if (p?.pricing_id && id === String(p.pricing_id) && p.coupon) return p.coupon;
            }
            return config.default_coupon || null;
        })();

        const trackerId = trackBeforeCheckout();

        fsHandlerRef.current.open({
            name: plan.title || plan.name,
            licenses: pricingEntry.licenses || 1,
            billing_cycle: billingCycle,
            currency,
            pricing_id: pricingEntry.id,
            ...(resolvedCoupon && { coupon: resolvedCoupon }),
            tracker_id: trackerId,
        });

        setCheckingOut(null);
    };

    const toggleMaximize = (e) => {
        e?.stopPropagation();
        setIsMaximized((v) => !v);
    };

    if (!isOpen) return null;

    const visiblePlans = pricingData?.plans?.filter((p) => !p.is_hidden) || [];
    const availableCycles = getAvailableCycles(visiblePlans);
    const pricingPlan = getPricingPlan();
    const isEventActive = !!pricingPlan?.is_event_sales;
    const activePlan = pricingPlan;

    return (
        <div
            style={{ ...overlayStyle, top: adminBarTop ? `${adminBarTop}px` : overlayStyle.top }}
            onClick={onClose}
            aria-modal="true"
            role="dialog"
        >
            <div className={`f-pricing-modal__container ${isMaximized ? 'is-maximized' : ''}`} onClick={(e) => e.stopPropagation()}>
                <div className="f-pricing-modal__topbar" role="toolbar" aria-label={__('Modal controls', 'jeg-elementor-kit')}>
                    <div className="f-pricing-modal__controls">
                        <button className="f-pricing-modal__dot is-max" title={__('Maximize', 'jeg-elementor-kit')} onClick={toggleMaximize} aria-pressed={isMaximized}>
                            {!isMaximized ? (
                                <svg width="6" height="6" viewBox="0 0 6 6" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                                    <path d="M5.83333 4.00359L5.81488 0.216779C5.81479 0.190851 5.80958 0.165146 5.79956 0.141137C5.78953 0.117128 5.77489 0.0952874 5.75647 0.0768641C5.73805 0.0584409 5.7162 0.0437981 5.6922 0.0337743C5.66819 0.0237505 5.64248 0.0185422 5.61655 0.0184487L1.82974 3.7135e-06C1.79071 -0.000235863 1.75259 0.0111217 1.72022 0.0326356C1.68786 0.0541495 1.66271 0.084851 1.64795 0.120849C1.6332 0.156846 1.62951 0.196518 1.63735 0.234836C1.64519 0.273153 1.66421 0.30839 1.692 0.33608L5.49725 4.14133C5.52494 4.16912 5.56018 4.18814 5.5985 4.19598C5.63681 4.20382 5.67649 4.20013 5.71248 4.18538C5.74848 4.17063 5.77918 4.14547 5.8007 4.11311C5.82221 4.08074 5.83357 4.04262 5.83333 4.00359Z" fill="white" />
                                    <path d="M3.7137e-06 1.82975L0.0184478 5.61655C0.0185413 5.64248 0.0237495 5.66819 0.0337733 5.69219C0.0437971 5.7162 0.058439 5.73805 0.0768622 5.75647C0.0952855 5.77489 0.117127 5.78954 0.141136 5.79956C0.165145 5.80958 0.19085 5.81479 0.216778 5.81489L4.00359 5.83333C4.04263 5.83357 4.08074 5.82221 4.11311 5.8007C4.14547 5.77918 4.17062 5.74848 4.18538 5.71249C4.20013 5.67649 4.20382 5.63682 4.19598 5.5985C4.18814 5.56018 4.16912 5.52494 4.14133 5.49725L0.336079 1.692C0.308389 1.66421 0.273154 1.64519 0.234836 1.63735C0.196519 1.62951 0.156845 1.6332 0.120848 1.64795C0.08485 1.66271 0.0541485 1.68786 0.0326346 1.72023C0.0111208 1.75259 -0.000235859 1.79071 3.7137e-06 1.82975Z" fill="white" />
                                </svg>
                            ) : (
                                <svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4.66674 0.190959L4.68467 3.87263C4.68476 3.89784 4.68983 3.92283 4.69957 3.94618C4.70932 3.96952 4.72355 3.99075 4.74146 4.00866C4.75938 4.02658 4.78061 4.04081 4.80395 4.05056C4.8273 4.0603 4.85229 4.06537 4.8775 4.06546L8.55917 4.08339C8.59712 4.08362 8.63418 4.07258 8.66565 4.05166C8.69711 4.03075 8.72157 4.0009 8.73591 3.9659C8.75026 3.9309 8.75385 3.89233 8.74622 3.85508C8.7386 3.81783 8.72011 3.78357 8.69309 3.75665L4.99348 0.0570389C4.96656 0.0300228 4.9323 0.0115306 4.89505 0.00390665C4.8578 -0.00371727 4.81923 -0.000129693 4.78423 0.0142147C4.74923 0.0285592 4.71938 0.0530145 4.69846 0.0844801C4.67755 0.115946 4.66651 0.153005 4.66674 0.190959Z" fill="white" />
                                    <path d="M4.08339 8.55917L4.06546 4.8775C4.06537 4.85229 4.0603 4.8273 4.05056 4.80396C4.04081 4.78061 4.02658 4.75938 4.00866 4.74147C3.99075 4.72356 3.96952 4.70932 3.94618 4.69957C3.92283 4.68983 3.89784 4.68476 3.87263 4.68467L0.190959 4.66674C0.153005 4.66651 0.115946 4.67755 0.08448 4.69847C0.0530143 4.71938 0.0285591 4.74923 0.0142146 4.78423C-0.000129815 4.81923 -0.00371769 4.8578 0.00390623 4.89505C0.0115301 4.93231 0.0300225 4.96657 0.0570385 4.99349L3.75665 8.69309C3.78357 8.72011 3.81782 8.7386 3.85508 8.74622C3.89233 8.75385 3.9309 8.75026 3.9659 8.73592C4.0009 8.72157 4.03075 8.69712 4.05166 8.66565C4.07258 8.63419 4.08362 8.59713 4.08339 8.55917Z" fill="white" />
                                </svg>

                            )}
                        </button>
                        <button className="f-pricing-modal__dot is-min" aria-hidden="true" title={__('Minimize', 'jeg-elementor-kit')}></button>
                        <button className="f-pricing-modal__dot is-close" id="pricing-close" aria-label={__('Close', 'jeg-elementor-kit')} title={__('Close', 'jeg-elementor-kit')} onClick={onClose}>
                            <svg width="6" height="6" viewBox="0 0 6 6" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                                <path d="M3.52792 2.85633L5.57356 0.815446C5.66315 0.725864 5.71347 0.604365 5.71347 0.477677C5.71347 0.350989 5.66315 0.22949 5.57356 0.139908C5.48398 0.0503265 5.36248 0 5.2358 0C5.10911 0 4.98761 0.0503265 4.89803 0.139908L2.85714 2.18555L0.816257 0.139908C0.726676 0.0503265 0.605177 -9.43898e-10 0.478489 0C0.351801 9.43899e-10 0.230302 0.0503265 0.14072 0.139908C0.0511385 0.22949 0.000812055 0.350989 0.000812054 0.477677C0.000812053 0.604365 0.0511385 0.725864 0.14072 0.815446L2.18636 2.85633L0.14072 4.89722C0.0961309 4.94144 0.0607395 4.99406 0.0365872 5.05203C0.012435 5.11 0 5.17218 0 5.23499C0 5.29779 0.012435 5.35997 0.0365872 5.41794C0.0607395 5.47591 0.0961309 5.52853 0.14072 5.57275C0.184946 5.61734 0.237562 5.65273 0.295534 5.67689C0.353506 5.70104 0.415687 5.71347 0.478489 5.71347C0.541291 5.71347 0.603472 5.70104 0.661444 5.67689C0.719416 5.65273 0.772032 5.61734 0.816257 5.57275L2.85714 3.52711L4.89803 5.57275C4.94225 5.61734 4.99487 5.65273 5.05284 5.67689C5.11081 5.70104 5.17299 5.71347 5.2358 5.71347C5.2986 5.71347 5.36078 5.70104 5.41875 5.67689C5.47672 5.65273 5.52934 5.61734 5.57356 5.57275C5.61815 5.52853 5.65355 5.47591 5.6777 5.41794C5.70185 5.35997 5.71429 5.29779 5.71429 5.23499C5.71429 5.17218 5.70185 5.11 5.6777 5.05203C5.65355 4.99406 5.61815 4.94144 5.57356 4.89722L3.52792 2.85633Z" fill="white" />
                            </svg>
                        </button>
                    </div>
                </div>

                <div className="f-pricing-modal f-pricing">
                    {loading && <p>{__('Loading pricing...', 'jeg-elementor-kit')}</p>}
                    {error && <p>{error}</p>}
                    {!loading && !error && (
                        <>
                            <Hero isEventActive={isEventActive} eventExpired={pricingPlan?.event_expired} />
                            <PlanCards
                                isEventActive={isEventActive}
                                availableCycles={availableCycles}
                                billingCycle={billingCycle}
                                onCycleChange={setBillingCycle}
                                visiblePlans={visiblePlans}
                                currency={currency}
                                activePlan={activePlan}
                                checkingOut={checkingOut}
                                onCheckout={handleCheckout}
                            />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PricingModal;
